/**
 * Payload entry component for DEX reader workflow.
 * Allows manual entry of payload identifiers (workflow, sample, stage IDs).
 * MVP solution due to no backend list endpoint for readers.
 */

/**
 * Escape HTML special characters to prevent XSS.
 * @param {string} str - String to escape
 * @returns {string} Escaped string
 */
function escapeHtml(str) {
  if (!str) return '';
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

/**
 * Render the payload entry form into a container.
 * @param {HTMLElement} container - Container element
 * @param {Object} state - Component state
 * @param {string} [state.workflowId=''] - Workflow ID value
 * @param {string} [state.sampleId=''] - Sample ID value
 * @param {string} [state.stageId=''] - Stage ID value
 * @param {string|null} [state.error=null] - Error message to display
 * @param {boolean} [state.loading=false] - Whether fetch is in progress
 */
export function renderPayloadEntry(container, state = {}) {
  const {
    workflowId = '',
    sampleId = '',
    stageId = '',
    error = null,
    loading = false
  } = state;

  const disabled = loading;
  const buttonText = loading ? 'Fetching...' : 'Fetch Payload';

  container.innerHTML = `
    <div class="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
      <p class="text-sm text-blue-800 mb-3">
        <strong>Note:</strong> Enter the payload identifiers provided by the sender.
      </p>
      <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label for="workflow-id" class="block text-xs text-gray-700 mb-1">
            Workflow ID <span class="text-gray-400 text-xs">(optional)</span>
          </label>
          <input
            type="text"
            id="workflow-id"
            class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            placeholder="e.g., workflow_123"
            value="${escapeHtml(workflowId)}"
            ${disabled ? 'disabled' : ''}
          />
        </div>
        <div>
          <label for="sample-id" class="block text-xs text-gray-700 mb-1">
            Sample ID <span class="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="sample-id"
            class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            placeholder="e.g., sample_456"
            value="${escapeHtml(sampleId)}"
            ${disabled ? 'disabled' : ''}
          />
        </div>
        <div>
          <label for="stage-id" class="block text-xs text-gray-700 mb-1">
            Stage ID <span class="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="stage-id"
            class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            placeholder="e.g., stage_789"
            value="${escapeHtml(stageId)}"
            ${disabled ? 'disabled' : ''}
          />
        </div>
      </div>
      ${error ? `<p class="text-red-500 text-sm mt-2">${escapeHtml(error)}</p>` : ''}
      <button
        id="fetch-payload-btn"
        type="button"
        class="mt-3 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        ${disabled ? 'disabled' : ''}
      >
        ${loading ? `
          <svg class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        ` : ''}
        ${buttonText}
      </button>
    </div>
  `;
}

/**
 * Initialize the payload entry component with event handlers.
 * @param {HTMLElement} container - Container element
 * @param {Function} onFetch - Callback when fetch is triggered: (identifiers, error) => void
 *   identifiers: { workflowId, sampleId, stageId } or null if validation fails
 *   error: string message or null if validation passes
 * @returns {Function} Cleanup function that removes event listeners
 */
export function initPayloadEntry(container, onFetch) {
  const fetchBtn = container.querySelector('#fetch-payload-btn');

  function handleFetchClick() {
    const workflowIdInput = container.querySelector('#workflow-id');
    const sampleIdInput = container.querySelector('#sample-id');
    const stageIdInput = container.querySelector('#stage-id');

    const workflowId = workflowIdInput?.value.trim() || 'any';
    const sampleId = sampleIdInput?.value.trim() || '';
    const stageId = stageIdInput?.value.trim() || '';

    // Validate required fields
    if (!sampleId || !stageId) {
      if (onFetch) {
        onFetch(null, 'Sample ID and Stage ID are required');
      }
      return;
    }

    // Valid - call callback with identifiers
    if (onFetch) {
      onFetch({ workflowId, sampleId, stageId }, null);
    }
  }

  if (fetchBtn) {
    fetchBtn.addEventListener('click', handleFetchClick);
  }

  // Return cleanup function
  return function cleanup() {
    if (fetchBtn) {
      fetchBtn.removeEventListener('click', handleFetchClick);
    }
  };
}
